import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from "react-i18next";
import ListingAPI from '../../axios/listing';
import '../../hs-theme.css';
import './guide.css';
import './advertising-guide.css';
import { ICON_PATHS } from '../../components/shared-components/icon-paths';

const coverPhoto = require('../../assets/images/home.jpg');
const watermark = require('../../assets/images/watermark.png');

const SECTIONS = ['s1', 's2', 's3', 's4', 's5', 's6'];
const MAX_PHOTOS = 20;
// [text key, icon path]
const STEPS = [
    ['step_1', ICON_PATHS.user],
    ['step_2', 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 10h4a1 1 0 0 1 1 1v10M3 21h18M8 8h3M8 12h3M8 16h3'],
    ['step_3', 'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21ZM12 7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z'],
    ['step_4', 'M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2ZM12 9.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z'],
    ['step_5', 'm5 12.5 4.5 4.5L19 7.5'],
];
const PHOTO_DO = ['do_1', 'do_2', 'do_3', 'do_4', 'do_5'];
const PHOTO_DONT = ['dont_1', 'dont_2', 'dont_3', 'dont_4'];
const DESC_ITEMS = ['desc_1', 'desc_2', 'desc_3', 'desc_4', 'desc_5', 'desc_6', 'desc_7', 'desc_8'];
const PRICE_TIPS = [
    ['tip_price', 'M4 20V10M10 20V4M16 20v-7M22 20H2'],
    ['tip_deal', 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z'],
    ['tip_location', 'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21ZM12 7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z'],
];
// the paid plans match the listing groups on the server: 1 standard, 2 premium
const PLANS = [
    { key: 'free', group: null, feats: ['free_feat_1', 'free_feat_2', 'free_feat_3'] },
    { key: 'standard', group: 1, feats: ['standard_feat_1', 'standard_feat_2', 'standard_feat_3'] },
    { key: 'premium', group: 2, feats: ['premium_feat_1', 'premium_feat_2', 'premium_feat_3'], dark: true },
];
const FAQ = ['faq_1', 'faq_2', 'faq_3', 'faq_4', 'faq_5'];
// a section counts as reached when its top is this close to the top of the window
const ACTIVE_OFFSET = 160;

const Icon = ({ path, size = 22, width = 1.9 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"><path d={path} /></svg>
);

const Hexagon = (props) => (
    <svg viewBox="0 0 40 44" fill="none" {...props}><path d="M20 1.5 38 11.75v20.5L20 42.5 2 32.25v-20.5Z" fill="#fff" /></svg>
);

const SectionHead = ({ number, children }) => (
    <div className='hs-article-head'>
        <span className='hs-article-number'>{number}</span>
        <h2>{children}</h2>
    </div>
);

export const AdvertisingGuide = () => {
    const { t } = useTranslation(["ad_guide"]);
    const [active, setActive] = useState(SECTIONS[0]);
    const [open, setOpen] = useState(0);
    const [prices, setPrices] = useState({});

    useEffect(() => {
        PLANS.filter(plan => plan.group).forEach(plan => {
            ListingAPI.getPromotionPrice(plan.group)
                .then(res => setPrices(prev => ({ ...prev, [plan.key]: res.data.price })))
                .catch(err => console.log(err));
        });
    }, []);

    // the contents list marks the section being read
    useEffect(() => {
        const onScroll = () => {
            let current = SECTIONS[0];
            SECTIONS.forEach(id => {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top < ACTIVE_OFFSET) current = id;
            });
            setActive(current);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const jump = (id) => (e) => {
        e.preventDefault();
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };
    const rich = (key, values) => <Trans t={t} i18nKey={key} values={values} components={{ b: <strong /> }} />;
    const price = (plan) => {
        if (!plan.group) return '0';
        const value = prices[plan.key];
        return value === undefined ? '…' : Math.round(Number(value)).toLocaleString('sr-RS');
    };

    return (
        <div className='hs hs-article'>
            <section className='hs-article-intro'>
                <div className='hs-article-intro-inner hs-ad-intro'>
                    <div className='hs-ad-intro-text'>
                        <nav className='hs-article-crumbs'>
                            <Link to='/'>{t("crumb_home")}</Link><span>/</span>
                            <Link to='/uputstvo'>{t("crumb_guide")}</Link><span>/</span>
                            <span className='current'>{t("crumb_current")}</span>
                        </nav>
                        <p className='hs-ad-eyebrow'>{t("eyebrow")}</p>
                        <h1>{t("title")}</h1>
                        <p className='hs-article-lead'>{t("lead")}</p>
                        <a href='#s5' className='hs-ad-outline' onClick={jump('s5')}>{t("packages_link")}</a>
                    </div>
                    <div className='hs-ad-stats'>
                        <div className='hs-ad-stat-free'>
                            <Hexagon className='hs-ad-stat-mark' width={180} height={198} />
                            <span className='hs-ad-stat-big'>1</span>
                            <span>{rich("stat_free")}</span>
                        </div>
                        <div className='hs-ad-stat'><span className='hs-ad-stat-value'>{MAX_PHOTOS}</span><span>{t("stat_photos")}</span></div>
                        <div className='hs-ad-stat'><span className='hs-ad-stat-value'>{t("stat_days_value")}</span><span>{t("stat_days")}</span></div>
                    </div>
                </div>
            </section>

            <div className='hs-article-body hs-ad-body'>
                <aside className='hs-article-toc'>
                    <p>{t("toc")}</p>
                    {SECTIONS.map((id, i) => (
                        <a key={id} href={`#${id}`} className={id === active ? 'on' : ''} onClick={jump(id)}>
                            <span>{i + 1}</span>{t(`${id}_toc`)}
                        </a>
                    ))}
                </aside>

                <article className='hs-article-article'>
                    <section id='s1' className='hs-article-section'>
                        <SectionHead number={1}>{t("s1_title")}</SectionHead>
                        <div className='hs-article-steps'>
                            {STEPS.map(([key, icon], i) => (
                                <div key={key} className={`hs-article-step ${i === STEPS.length - 1 ? 'last' : ''}`}>
                                    <div className='hs-article-step-rail'>
                                        <span className='hs-article-step-number'><Icon path={icon} size={20} /></span>
                                        <span className='hs-article-step-line'></span>
                                    </div>
                                    <div className='hs-article-card hs-article-step-card'>
                                        <span className='hs-ad-step-label'>{t("step_label", { n: i + 1 })}</span>
                                        <span className='hs-ad-step-title'>{t(`${key}_title`)}</span>
                                        <span className='hs-ad-step-text'>{t(`${key}_text`, { count: MAX_PHOTOS })}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section id='s2' className='hs-article-section'>
                        <SectionHead number={2}>{t("s2_title")}</SectionHead>
                        <p className='hs-article-text'>{rich("photo_intro")}</p>
                        <div className='hs-ad-grid'>
                            <div className='hs-article-card hs-ad-list'>
                                <span className='hs-ad-list-head do'><span><Icon path="m5 12.5 4.5 4.5L19 7.5" size={13} width={3.2} /></span>{t("do_title")}</span>
                                {PHOTO_DO.map(key => <span key={key} className='hs-ad-list-item do'>{t(key)}</span>)}
                            </div>
                            <div className='hs-article-card hs-ad-list'>
                                <span className='hs-ad-list-head dont'><span>×</span>{t("dont_title")}</span>
                                {PHOTO_DONT.map(key => <span key={key} className='hs-ad-list-item dont'>{t(key)}</span>)}
                            </div>
                        </div>
                        <div className='hs-ad-cover'>
                            <div className='hs-ad-cover-photo' style={{ backgroundImage: `url(${coverPhoto})` }}>
                                <img src={watermark} alt='' />
                                <span>{t("cover_badge")}</span>
                            </div>
                            <p>{rich("cover_text")}</p>
                        </div>
                        <div className='hs-article-important'>
                            <span className='hs-article-important-icon'><Icon path="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM12 7v5l3 2" size={17} width={2.2} /></span>
                            <p>{rich("photo_important")}</p>
                        </div>
                    </section>

                    <section id='s3' className='hs-article-section'>
                        <SectionHead number={3}>{t("s3_title")}</SectionHead>
                        <p className='hs-article-text'>{rich("desc_intro")}</p>
                        <div className='hs-article-card hs-ad-titles'>
                            <div><span className='hs-ad-pill good'>{t("good_title")}</span><span className='hs-ad-title-good'>{t("good_title_example")}</span></div>
                            <div><span className='hs-ad-pill bad'>{t("bad_title")}</span><span className='hs-ad-title-bad'>{t("bad_title_example")}</span></div>
                        </div>
                        <div className='hs-ad-desc'>
                            <span>{t("desc_list")}</span>
                            <div>
                                {DESC_ITEMS.map(key => (
                                    <span key={key} className='hs-ad-chip'><span><Icon path="m5 12.5 4.5 4.5L19 7.5" size={11} width={3.2} /></span>{t(key)}</span>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section id='s4' className='hs-article-section'>
                        <SectionHead number={4}>{t("s4_title")}</SectionHead>
                        <div className='hs-ad-grid'>
                            {PRICE_TIPS.map(([key, icon]) => (
                                <div key={key} className='hs-article-card hs-ad-tip'>
                                    <span className='hs-ad-tip-icon'><Icon path={icon} /></span>
                                    <span className='hs-ad-tip-title'>{t(`${key}_title`)}</span>
                                    <span className='hs-ad-tip-text'>{t(`${key}_text`)}</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section id='s5' className='hs-article-section'>
                        <SectionHead number={5}>{t("s5_title")}</SectionHead>
                        <p className='hs-article-text'>{rich("plans_intro")}</p>
                        <div className='hs-ad-plans'>
                            {PLANS.map(plan => (
                                <div key={plan.key} className={`hs-ad-plan ${plan.key} ${plan.dark ? 'dark' : ''}`}>
                                    {plan.dark && <span className='hs-ad-plan-hot'>{t("plan_hot")}</span>}
                                    <span className='hs-ad-plan-name'>{t(`${plan.key}_name`)}</span>
                                    <span className='hs-ad-plan-price'><span>{price(plan)}</span>{t(`${plan.key}_unit`)}</span>
                                    <span className='hs-ad-plan-text'>{t(`${plan.key}_text`)}</span>
                                    <div className='hs-ad-plan-feats'>
                                        {plan.feats.map(feat => <span key={feat}><span>✓</span>{t(feat, { count: MAX_PHOTOS })}</span>)}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='hs-ad-plans-foot'>
                            <span>{t("plans_note")}</span>
                            <Link to='/korisnik/oglasavanje'>{t("agencies_link")} →</Link>
                        </div>
                    </section>

                    <section id='s6' className='hs-article-section'>
                        <SectionHead number={6}>{t("s6_title")}</SectionHead>
                        <div className='hs-article-card hs-ad-faq'>
                            {FAQ.map((key, i) => {
                                const on = open === i;
                                return (
                                    <div key={key}>
                                        <button aria-expanded={on} className={on ? 'on' : ''} onClick={() => setOpen(on ? -1 : i)}>
                                            {t(`${key}_q`)}
                                            <span>{on ? '−' : '+'}</span>
                                        </button>
                                        {on && <p>{t(`${key}_a`)}</p>}
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <section className='hs-article-cta hs-ad-cta'>
                        <Hexagon className='hs-article-cta-mark' width={240} height={264} />
                        <span className='hs-ad-cta-text'>
                            <span className='hs-article-cta-title'>{t("cta_title")}</span>
                            <span>{t("cta_text")}</span>
                        </span>
                        <Link to='/postavi-oglas' className='hs-ad-cta-button'>+ {t("cta_button")}</Link>
                    </section>
                </article>
            </div>
        </div>
    );
};
